import { CurrencyDropdown, StatusDropdown } from "components";
import ExchangeRatePicker from "components/exchangerate/ExchangeRatePicker";
import { type BookkeepingCurrency } from "components/exchangerate/hooks";
import LabelledDivRow from "components/LabelledDivRow";
import { type ShowDates } from "components/showdatepicker/models";
import ShowDatePicker from "components/showdatepicker/ShowDatePicker";
import TimeOfPerformance from "components/showdatepicker/TimeOfPerformance";
import UrlInput from "components/urlinput";
import { useValidateIsNotNull } from "formvalidation/hooks";
import React from "react";
import { Input } from "semantic-ui-react";
import TagsSelect from "components/Tag/TagsSelect";
import { blurAndSubmitOnEnter } from "utilities/reactUtils";
import PublicationDropdown from "components/dropdowns/reactselect/PublicationDropdown";
import CostCenterModalPicker from "components/Picker/CostCenterPicker/CostCenterModalPicker";
import UserModalPicker from "components/Picker/UserPicker/UserModalPicker";
import ProductionModalPicker from "components/Picker/ProductionPicker/ProductionModalPicker";
import { useHasAnyTags } from "components/Tag/hooks";
import { ProjectCodeInput } from "components/ProjectCodeInput";
import BookkeepingPaginatedDropdown from "components/dropdowns/paginated/BookkeepingPaginatedDropdown";

interface Props {
  production: server.dto.ProductionSmallV2
  onProductionChanged: (production: server.dto.ProductionSmallV2) => void

  dates: ShowDates
  onDatesChanged: (newDates: ShowDates, hasErrors: boolean) => void
  onTimeOfPerformanceChanged: (newTimeOfPerformance: string) => void

  status: server.dto.StatusSmall
  onStatusChanged: (status: server.dto.StatusSmall) => void
  statuses: server.dto.StatusSmall[]

  initialEventName: string
  onEventNameChanged: (eventName: string) => void

  eventWebsite: string
  onEventWebsiteChanged: (eventWebsite: string) => void

  publication: server.dto.PublicationStatuses
  onPublicationChanged: (publication: server.dto.PublicationStatuses) => void

  costCenter: server.dto.CostCenter
  onCostCenterChanged: (costCenter: server.dto.CostCenter) => void

  projectCode: string
  onProjectCodeChanged: (projectCode: string) => void

  tags: server.dto.TagSmall[]
  onTagToggle: (tags: server.dto.TagSmall[]) => void

  owner: server.dto.UserSmall
  onOwnerChanged: (owner: server.dto.UserSmall) => void

  bookkeepingCurrency: BookkeepingCurrency
}

const MainDetailsPanel = (props: Props) => {
  const [eventName, setEventName] = React.useState(props.initialEventName ?? "");

  const hasTags = useHasAnyTags("show");

  const bookkeepingValidatorKey = "bookkeeping";
  const bookkeepingValidation = useValidateIsNotNull({ key: bookkeepingValidatorKey, title: RESX.Bookkeeping.resxBookkeeping, value: props.bookkeepingCurrency.bookkeeping });

  return <>
    {site.currentEnvironment.production.enabled && <LabelledDivRow label={site.currentEnvironment.production.singular}>
      <ProductionModalPicker
        value={props.production}
        onChange={props.onProductionChanged}
        canCreateNew
        validatorKey="production"
        required={false}
      />
    </LabelledDivRow>}

    <LabelledDivRow label={RESX.DateTime.lblDate}>
      <ShowDatePicker
        validatorKey="showDates"
        dates={props.dates}
        onDatesChanged={props.onDatesChanged}
      />
    </LabelledDivRow>

    <LabelledDivRow label={RESX.Show.TimeOfPerformance}>
      <TimeOfPerformance
        showDates={props.dates}
        onTimeOfPerformanceChanged={props.onTimeOfPerformanceChanged}
      />
    </LabelledDivRow>

    <LabelledDivRow label={RESX.Status.resxStatus}>
      <StatusDropdown
        options={props.statuses}
        value={props.status}
        onChange={props.onStatusChanged}
        mainArea="show"
      />
    </LabelledDivRow>

    <LabelledDivRow label={RESX.Show.NameOfEvent}>
      <Input
        fluid
        maxLength={serverReference.constants.show.maxEventNameLength}
        value={eventName}
        onChange={(_, { value }) => { setEventName(value); }}
        onBlur={() => { props.onEventNameChanged(eventName); }}
        onKeyDown={blurAndSubmitOnEnter}
      />
    </LabelledDivRow>

    <LabelledDivRow label={RESX.Show.EventWebsite}>
      <UrlInput
        value={props.eventWebsite}
        onValueChange={props.onEventWebsiteChanged}
        validatorKey="eventWebsite"
      />
    </LabelledDivRow>

    <LabelledDivRow label={RESX.SelectionManager.Publication}>
      <PublicationDropdown
        value={props.publication}
        onChange={props.onPublicationChanged}
      />
    </LabelledDivRow>

    <LabelledDivRow label={RESX.Bookkeeping.resxBookkeeping} rowRef={bookkeepingValidation.ref}>
      <BookkeepingPaginatedDropdown
        value={props.bookkeepingCurrency.bookkeeping}
        onChange={b => {
          props.bookkeepingCurrency.onBookkeepingChange(b);
        }}
        config={{
          hasError: bookkeepingValidation.error.hasError
        }}
        selectIfOnlyOption
      />
    </LabelledDivRow>
    <LabelledDivRow label={RESX.Financial.Currency}>
      <CurrencyDropdown
        value={props.bookkeepingCurrency.currency}
        onChange={props.bookkeepingCurrency.onCurrencyChange}
      />
    </LabelledDivRow>
    {props.bookkeepingCurrency.isExchangeRateVisible && <LabelledDivRow label={RESX.Financial.ExchangeRate}>
      <ExchangeRatePicker
        bookkeepingCurrency={props.bookkeepingCurrency}
        validatorKey="exchangeRate"
      />
    </LabelledDivRow>}

    {site.currentEnvironment.hasCostCenters && <LabelledDivRow label={RESX.CostCenter.resxCostCenter}>
      <div>
        <CostCenterModalPicker
          value={props.costCenter}
          onChange={props.onCostCenterChanged}
          required
          validatorKey="costCenter"
          canCreateNew={false}
        />
      </div>
    </LabelledDivRow>}

    {site.currentEnvironment.projectCode !== "hidden" && <LabelledDivRow label={RESX.Show.ProjectCode}>
      <ProjectCodeInput
        value={props.projectCode}
        onChange={props.onProjectCodeChanged}
        canCreateNew
      />
    </LabelledDivRow>}


    {hasTags && <LabelledDivRow label={RESX.Tag.Tags} bigPadding>
      <TagsSelect
        value={props.tags}
        mainArea="show"
        onChange={props.onTagToggle}
      />
    </LabelledDivRow>}

    <LabelledDivRow label={RESX.GeneralLabels.Owner}>
      <UserModalPicker
        value={props.owner}
        onChange={props.onOwnerChanged}
        canChooseDeactivated
        validatorKey="owner"
        required
        canCreateNew={false}
      />
    </LabelledDivRow>
  </>;
};

export default MainDetailsPanel;
